import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonitorIcon from '@mui/icons-material/Monitor';
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { useAuthProvider, useRecordContext } from 'react-admin';
import type { OpenBalenaAuthProvider, OpenBalenaSession } from '../authProvider/openbalenaAuthProvider';
import environment from '../lib/reactAppEnv';
import type { ResourceRecord } from '../types/resource';

const REFRESH_INTERVAL_MS = 30000; // refresh screenshot every 30 s

export const DeviceVncPreview: React.FC = () => {
  const record = useRecordContext<ResourceRecord>();
  const authProvider = useAuthProvider<OpenBalenaAuthProvider>();
  const [screenshotUrl, setScreenshotUrl] = React.useState('');
  const [fullUrl, setFullUrl] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const isOnline = record?.['api heartbeat state'] === 'online';

  const buildUrl = React.useCallback(() => {
    if (!record || !authProvider || !isOnline) return '';
    const session: OpenBalenaSession | undefined = authProvider?.getSession?.();
    const jwt = session?.jwt ?? '';
    if (!jwt || typeof record.uuid !== 'string') return '';
    const remoteHost = environment.REACT_APP_OPEN_BALENA_REMOTE_URL ?? '';
    // Append a timestamp cache-buster so the browser re-fetches each time
    // thumb=1 for panel preview (fast), full for modal
    return `${remoteHost}/screenshot/${record.uuid}?jwt=${encodeURIComponent(jwt)}&t=${Date.now()}`;
  }, [record, authProvider, isOnline]);

  const refresh = React.useCallback(() => {
    const base = buildUrl();
    if (!base) return;
    setLoading(true);
    // Thumb for panel, full for modal
    setScreenshotUrl(base.replace('&t=', '&thumb=1&t='));
    setFullUrl(base);
  }, [buildUrl]);

  // Initial load + periodic refresh
  React.useEffect(() => {
    refresh();
    if (!isOnline) return;
    const timer = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [refresh, isOnline]);

  if (!record) return null;

  return (
    <>
      <Tooltip title={isOnline ? 'Display preview — click to expand' : 'Device offline'} placement="top">
        <Box
          onClick={() => isOnline && screenshotUrl && !loading && setOpen(true)}
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16/9',
            background: '#111',
            cursor: isOnline && screenshotUrl && !loading ? 'pointer' : 'default',
            overflow: 'hidden',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover .preview-overlay': { opacity: isOnline && screenshotUrl ? 1 : 0 },
          }}
        >
          {screenshotUrl ? (
            <Box
              component="img"
              src={screenshotUrl}
              alt="Display preview"
              onLoad={() => setLoading(false)}
              onError={() => setLoading(false)}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.disabled' }}>
              <MonitorIcon sx={{ fontSize: 40 }} />
            </Box>
          )}

          {/* Loading spinner overlay */}
          {loading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
              <CircularProgress size={28} sx={{ color: 'white' }} />
            </Box>
          )}

          {/* Hover overlay */}
          <Box
            className="preview-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.35)',
              opacity: 0,
              transition: 'opacity 0.15s',
              pointerEvents: 'none',
            }}
          >
            <OpenInFullIcon sx={{ color: 'white', fontSize: 36 }} />
          </Box>
        </Box>
      </Tooltip>

      {/* Expanded modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        sx={{ '& .MuiPaper-root': { width: '90vw', height: '90vh', maxWidth: 'none', background: '#000' } }}
      >
        <DialogTitle sx={{ py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white', background: '#111' }}>
          Display — {record['device name']}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={refresh} size="small" sx={{ color: 'white' }} title="Refresh">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, flex: 1, display: 'flex', overflow: 'hidden', background: '#000' }}>
          {fullUrl && (
            <Box
              component="img"
              src={fullUrl}
              alt="Display preview"
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeviceVncPreview;
